// buildParamConformance.js — per-GAM-param Checks detail rows.
//
// buildParamConformanceRows(results, dictionary, buSummary) turns a session's
// compiled GAM-request check results into one row per ad-tag query param,
// with the density the Inspect tab has for a single request: what the param
// should be (spec layer, always on, and BU layer, only when an App Pack
// matched), what this session actually sent, and how many ELIGIBLE requests
// FAIL it. Pure, ESM, never throws — see docs/gam-param-conformance-spec.md
// §3.5 for the column contract and .claude/skills/gam-param-conformance for
// the frozen Contract D this implements.
//
// ── The two counts that matter (spec §1.1) ─────────────────────────────────
// `matched` on a check result is "eligible requests" (rows in scope after
// target.requestType + target.where). `failures.length` is "failing
// requests". `found` is a DIFFERENT metric (an anti-vacuous-pass signal on
// evidence rows) and must never be read here — three real burst checks once
// reported found:430 against true counts of 368/197/21. Evidence arrays are
// capped at 50 rows and must never be counted either.
//
// ── Wiring contract ──────────────────────────────────────────────────────
// `results` is an array of per-param check-result-shaped objects. Each item
// needs a `key` (the GAM query param it evaluates, e.g. check.assert.param)
// to be renderable; items without one are ignored. Beyond `key`, an item may
// carry: `matched` (eligible count), `failures` (array — preferred) or
// `failureCount` (number — the slimmed shape prepared.results actually ships
// over the wire; used only when `failures` is absent), `status`
// ('pass'|'warn'|'fail'|'info'|'n/a'), `severity`, and optionally `expected`
// (a pre-formatted human string) or `statement`/`pattern`/`values` to build
// one from. AgentPanel.jsx is responsible for merging suite check
// definitions (which carry `assert.param`/`statement`) with the run's
// results before calling this — the merge is wiring, not builder logic.
//
// `dictionary` is GAM_PARAM_DICTIONARY (frontend/src/utils/gamParams.js) —
// its `name`/`meaning` prose is reused verbatim, never rewritten here.
//
// `buSummary` is Contract B's `byKey` map (`{ [key]: { eligible, failing,
// severity, expected, observed } }`), or null when no App Pack matched this
// session's `iu`. A wrapper `{ byKey }` is also accepted defensively. When
// null, every `bu*` field on every row is null — the BU column renders "—",
// never a failure; that is the correct state for an unconfigured user.
//
// ── The judgement calls this file makes (not specified verbatim upstream) ──
// - eligible-0 is rendered as its own verdict, 'not_evaluated' — distinct
//   from 'pass'. A check that never saw a request in scope has not vouched
//   for anything; conflating that with "passed" is the single worst thing
//   this view could do (spec §3.1/§G3).
// - When more than one check targets the same param key (e.g. `sz` has two
//   baseline checks), the WORST one wins — a real fail on one check must
//   never be hidden behind a sibling check that matched 0 rows.
// - The BU layer is capped at 'warn' when it feeds the combined `verdict`
//   field (skill §6 settled decision 2: a BU fail is our convention, not
//   GAM's, and must never outrank a spec-layer verdict here). `buSeverity`
//   itself is reported uncapped, as authored in the pack, for transparency.
// - Layers are never averaged (spec §3.1) — `verdictLayer` says which layer
//   produced the combined `verdict` so the two claims stay distinguishable.

const STATUS_RANK = { fail: 4, warn: 3, info: 2, pass: 1, not_evaluated: 0 };
const NOT_EVALUATED = 'not_evaluated';

/** evaluateCheck's status vocabulary, collapsed to the five verdicts this view renders. */
function normalizeStatus(status) {
  if (status === 'fail' || status === 'error') return 'fail';
  if (status === 'warn' || status === 'warning') return 'warn';
  if (status === 'info') return 'info';
  if (status === 'pass' || status === 'ok') return 'pass';
  return NOT_EVALUATED; // 'n/a', undefined, anything unrecognized
}

/** -1 = no data at all (not even attempted), 0..4 = the five renderable verdicts. */
function rankOf(verdict) {
  return Object.prototype.hasOwnProperty.call(STATUS_RANK, verdict) ? STATUS_RANK[verdict] : -1;
}

/** `failures.length`, never `found`. Falls back to the slimmed `failureCount`
 * the wire shape carries when the `failures` array itself wasn't sent. */
function failingCountOf(result) {
  if (result && Array.isArray(result.failures)) return result.failures.length;
  if (result && typeof result.failureCount === 'number' && Number.isFinite(result.failureCount)) {
    return Math.max(0, Math.trunc(result.failureCount));
  }
  return 0;
}

/** `matched` — eligible requests. Never negative, never NaN. */
function eligibleCountOf(result) {
  const n = Number(result && result.matched);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : 0;
}

function expectedTextOf(result) {
  if (!result) return null;
  if (typeof result.expected === 'string' && result.expected) return result.expected;
  if (typeof result.statement === 'string' && result.statement) return result.statement;
  if (typeof result.pattern === 'string' && result.pattern) return `matches ${result.pattern}`;
  if (Array.isArray(result.values) && result.values.length) return `one of ${result.values.join(', ')}`;
  return null;
}

function keyOf(result) {
  return (result && typeof result.key === 'string' && result.key) ? result.key : null;
}

/** Among several check results targeting the same param, the one that
 * actually ran against eligible rows and found the worst status wins. A
 * result with 0 eligible rows never outranks one that did run — otherwise a
 * sibling check that happened to match nothing could bury a real failure. */
function worstOf(list) {
  let best = null;
  let bestRank = -1;
  for (const r of list) {
    const eligible = eligibleCountOf(r);
    const effectiveRank = eligible === 0 ? -1 : rankOf(normalizeStatus(r && r.status));
    if (effectiveRank > bestRank) { bestRank = effectiveRank; best = r; }
  }
  return best || list[0] || null;
}

function buildSpecLayer(list) {
  if (!Array.isArray(list) || !list.length) {
    return { expected: null, severity: null, eligible: null, failing: null, verdict: null };
  }
  const chosen = worstOf(list);
  const eligible = eligibleCountOf(chosen);
  const failing = failingCountOf(chosen);
  return {
    expected: expectedTextOf(chosen),
    severity: (chosen && typeof chosen.severity === 'string') ? chosen.severity : null,
    eligible,
    failing,
    verdict: eligible === 0 ? NOT_EVALUATED : normalizeStatus(chosen && chosen.status),
  };
}

function buildBuLayer(entry) {
  if (!entry || typeof entry !== 'object') {
    return { expected: null, severity: null, eligible: null, failing: null, verdict: null };
  }
  const eligible = eligibleCountOf({ matched: entry.eligible });
  const failing = failingCountOf({ failureCount: entry.failing });
  const severity = (typeof entry.severity === 'string' && entry.severity) ? entry.severity : null;
  let verdict;
  if (eligible === 0) verdict = NOT_EVALUATED;
  else if (failing > 0) verdict = (severity === 'fail' || severity === 'warn' || severity === 'info') ? severity : 'warn';
  else verdict = 'pass';
  return {
    expected: (typeof entry.expected === 'string' && entry.expected) ? entry.expected : null,
    severity,
    eligible,
    failing,
    verdict,
  };
}

/** Worst-of-two-layers, never averaged. The BU layer is capped at 'warn' here
 * (skill §6 decision 2) — its true declared severity still reaches the row
 * via buSeverity, just not the combined verdict. */
function pickVerdict(specLayer, buLayer) {
  const specRank = rankOf(specLayer.verdict);
  const buRank = Math.min(rankOf(buLayer.verdict), STATUS_RANK.warn);
  if (specRank < 0 && buRank < 0) return { verdict: null, verdictLayer: null };
  if (specRank >= buRank) return { verdict: specLayer.verdict, verdictLayer: 'spec' };
  const cappedBuVerdict = buLayer.verdict === 'fail' ? 'warn' : buLayer.verdict;
  return { verdict: cappedBuVerdict, verdictLayer: 'bu' };
}

export function buildParamConformanceRows(results, dictionary, buSummary) {
  try {
    const dict = (dictionary && typeof dictionary === 'object') ? dictionary : {};
    const resultList = Array.isArray(results) ? results.filter((r) => r && typeof r === 'object') : [];

    // buSummary is the byKey map itself (Contract D); a { byKey } wrapper is
    // also accepted so this degrades gracefully if a caller passes Contract
    // B's raw return value instead of unwrapping it first.
    const buMap = (buSummary && typeof buSummary === 'object')
      ? ((buSummary.byKey && typeof buSummary.byKey === 'object') ? buSummary.byKey : buSummary)
      : null;

    const specByKey = new Map();
    for (const r of resultList) {
      const key = keyOf(r);
      if (!key) continue;
      if (!specByKey.has(key)) specByKey.set(key, []);
      specByKey.get(key).push(r);
    }

    const keys = new Set([
      ...specByKey.keys(),
      ...(buMap ? Object.keys(buMap) : []),
    ]);

    const rows = [];
    for (const key of keys) {
      const specLayer = buildSpecLayer(specByKey.get(key));
      const buLayer = buMap ? buildBuLayer(buMap[key]) : { expected: null, severity: null, eligible: null, failing: null, verdict: null };
      const { verdict, verdictLayer } = pickVerdict(specLayer, buLayer);
      const dictEntry = dict[key];

      rows.push({
        key,
        label: (dictEntry && typeof dictEntry.name === 'string' && dictEntry.name) ? dictEntry.name : key,
        meaning: (dictEntry && typeof dictEntry.meaning === 'string' && dictEntry.meaning) ? dictEntry.meaning : null,
        specExpected: specLayer.expected,
        specSeverity: specLayer.severity,
        specEligible: specLayer.eligible,
        specFailing: specLayer.failing,
        buExpected: buMap ? buLayer.expected : null,
        buSeverity: buMap ? buLayer.severity : null,
        buEligible: buMap ? buLayer.eligible : null,
        buFailing: buMap ? buLayer.failing : null,
        verdict,
        verdictLayer,
      });
    }

    // Worst first — the rows worth looking at surface without scrolling past
    // a session's worth of quiet passes; alphabetical beneath that so the
    // order is stable and predictable rather than reshuffling on rerun.
    rows.sort((a, b) => {
      const byVerdict = rankOf(b.verdict) - rankOf(a.verdict);
      return byVerdict !== 0 ? byVerdict : a.key.localeCompare(b.key);
    });

    return rows;
  } catch {
    return [];
  }
}

export default buildParamConformanceRows;
